import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, Buildings, Users, TrendUp, ArrowRight } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { JobCard } from "@/components/JobCard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRegion } from "@/context/RegionContext";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/d98a791f-86f0-401b-90f0-5668ae659e17/images/a6866fa698429c49a14af6aded1df0867fde791b37b085accdfab1b4636c3c60.jpeg";
const POPULAR = ["Remote", "IT", "NHS", "Marketing", "Accounting", "Engineering", "Sales"];

const STATS = [
  { icon: Briefcase, color: "text-blue-600 bg-blue-50", value: "25,000+", label: "Live Jobs" },
  { icon: Buildings, color: "text-emerald-600 bg-emerald-50", value: "8,000+", label: "Companies" },
  { icon: Users, color: "text-violet-600 bg-violet-50", value: "120,000+", label: "Job Seekers" },
  { icon: TrendUp, color: "text-orange-500 bg-orange-50", value: "Updated Daily", label: "New opportunities" },
];

export default function Home() {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const { user } = useAuth();
  const { region, meta } = useRegion();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/jobs/featured", { params: { country: region } }).then(({ data }) => setJobs(data.jobs)).catch(() => {});
    api.get("/jobs/filters", { params: { country: region } }).then(({ data }) => setCategories(data.categories)).catch(() => {});
  }, [region]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/me/saved-jobs").then(({ data }) => setSavedIds(data.saved_ids)).catch(() => {});
    }
  }, [user]);

  const onToggleSaved = (id, isSaved) =>
    setSavedIds((prev) => (isSaved ? [...prev, id] : prev.filter((x) => x !== id)));

  return (
    <div className="App">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 pb-10 pt-16 md:grid-cols-2 md:px-8 md:pt-20">
          <div className="animate-fade-up">
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              The Network Built
              <br />
              Around <span className="text-primary">Your Career.</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
              Search thousands of live vacancies from top employers across the UK.
              Your next opportunity starts here.
            </p>
          </div>
          <div className="relative hidden md:block">
            <img
              src={HERO_IMG}
              alt="Professionals working together"
              className="h-[380px] w-full object-cover"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, black 22%), linear-gradient(to bottom, black 78%, transparent 100%), linear-gradient(to top, black 92%, transparent 100%)",
                WebkitMaskComposite: "source-in",
                maskImage:
                  "linear-gradient(to right, transparent 0%, black 22%), linear-gradient(to bottom, black 78%, transparent 100%), linear-gradient(to top, black 92%, transparent 100%)",
                maskComposite: "intersect",
              }}
            />
            <div className="pointer-events-none absolute inset-0 bg-navy/10 mix-blend-multiply" />
          </div>
        </div>

        {/* Search bar */}
        <div className="relative z-10 mx-auto max-w-6xl px-4 pb-16 md:px-8">
          <SearchBar categories={categories} />
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
            <span className="font-medium text-white/50">Popular searches:</span>
            {POPULAR.map((p) => (
              <button
                key={p}
                onClick={() => navigate(`/jobs?q=${encodeURIComponent(p)}`)}
                className="rounded-full px-2 py-0.5 transition-colors hover:text-accent"
                data-testid={`popular-search-${p.toLowerCase()}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-20 mx-auto -mt-8 max-w-6xl px-4 md:px-8">
        <div className="grid grid-cols-2 gap-4 rounded-2xl border border-border bg-card p-6 shadow-sm md:grid-cols-4 md:p-8">
          {STATS.map((s, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`stat-${i}`}>
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.color}`}>
                <s.icon size={22} weight="fill" />
              </span>
              <div>
                <div className="font-heading text-lg font-bold leading-none text-foreground">{s.value}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured jobs */}
      <section className="mx-auto mt-16 max-w-6xl px-4 md:px-8">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">Featured Jobs</h2>
            <p className="mt-1 text-sm text-muted-foreground">Showing vacancies in the {meta.label}</p>
          </div>
          <Link to="/jobs" className="flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="view-all-jobs-link">
            View all jobs <ArrowRight size={16} />
          </Link>
        </div>

        <div className="mt-6 space-y-3">
          {jobs.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
              No jobs posted yet. Check back soon.
            </div>
          )}
          {jobs.map((job, i) => (
            <JobCard
              key={job.id}
              job={job}
              index={i}
              saved={savedIds.includes(job.id)}
              onToggleSaved={onToggleSaved}
            />
          ))}
        </div>

        {jobs.length > 0 && (
          <Link
            to="/jobs"
            className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-4 text-sm font-medium text-primary transition-colors hover:border-primary"
          >
            View all jobs <ArrowRight size={16} />
          </Link>
        )}
      </section>

      <Footer />
    </div>
  );
}
